import {
  CardContent,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { AuthState } from '../../../state/auth';
import { AppState } from '../../../state/configureStore';
import {
  clearSalesGroup,
  createSalesGroup,
  fetchSalesGroup,
  SalesGroup,
  SalesGroupState,
  updateSalesGroup,
} from '../../../state/salesGroup';
import {
  FixedPositionButtons,
  Loading,
  PageContainer,
  PageTitle,
} from '../../components';
import useStyles from './salesGroupForm.style';

const SalesGroupSchema = Yup.object().shape({
  name: Yup.string()
    .strict(true)
    .trim()
    .max(20, 'Name is exceeded character limit(20)')
    .required('Name is required'),
  description: Yup.string().nullable(true),
  ownerId: Yup.number().required('Owner is required'),
});

interface SalesGroupFormRouteParamsProps {
  id: string;
}

interface SalesGroupFormProps
  extends RouteComponentProps<SalesGroupFormRouteParamsProps> {}

const SalesGroupForm: React.FC<SalesGroupFormProps> = (props) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { match, history } = props;
  const {
    params: { id: paramsId },
  } = match;
  const isUpdateMode = paramsId !== undefined;
  const { user, loading: authIsLoading } = useSelector<AppState, AuthState>(
    (state) => state.auth,
  );
  const { item: salesGroup, loading: salesGroupIsLoading } = useSelector<
    AppState,
    SalesGroupState
  >((state) => state.salesGroup);

  const redirectToSalesGroupList = () => {
    history.goBack();
  };

  const handleSubmit = (values: SalesGroup, actions: FormikHelpers<SalesGroup>) => {
    if (isUpdateMode) {
      dispatch(updateSalesGroup({ values, history }));
    } else {
      dispatch(createSalesGroup({ values, history }));
    }
    actions.setSubmitting(false);
  };

  useEffect(() => {
    if (isUpdateMode) {
      dispatch(fetchSalesGroup(+paramsId));
    } else {
      dispatch(clearSalesGroup());
    }
    return function cleanUp() {
      dispatch(clearSalesGroup());
    };
  }, [isUpdateMode, paramsId, dispatch]);

  const defaultValues: SalesGroup = {
    owner: user,
    ownerId: user ? user.id : undefined,
    active: true,
  };
  const isLoading = authIsLoading || salesGroupIsLoading;
  return (
    <PageContainer>
      <Loading isLoading={isLoading}>
        <Formik
          enableReinitialize={true}
          initialValues={{ ...defaultValues, ...salesGroup }}
          validationSchema={SalesGroupSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, errors, touched, submitForm }) => {
            return (
              <Form>
                <PageTitle>
                  {!isUpdateMode
                    ? `New Sales Group`
                    : `Update Sales Group ${salesGroup.name}`}
                </PageTitle>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2">Name</Typography>
                      <TextField
                        name="name"
                        type="input"
                        variant="outlined"
                        fullWidth
                        value={values.name || ''}
                        onChange={handleChange}
                        error={!!errors.name && !!touched.name}
                        helperText={errors.name}
                        size="small"
                        placeholder="Sales Group Name"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Description</Typography>
                      <TextField
                        name="description"
                        type="input"
                        variant="outlined"
                        fullWidth
                        value={values.description || ''}
                        onChange={handleChange}
                        error={!!errors.description && !!touched.description}
                        helperText={errors.description}
                        size="small"
                        placeholder="Sales Group Description"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Owner</Typography>
                      <TextField
                        name="owner"
                        type="input"
                        variant="outlined"
                        fullWidth
                        value={
                          values.owner
                            ? `${values.owner!!.firstName} ${values.owner!!.lastName}`
                            : ''
                        }
                        InputProps={{
                          readOnly: true,
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Status</Typography>
                      <Select
                        name="active"
                        value={values.active}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        className={classes.customSelect}
                      >
                        <MenuItem value={'true'}>Active</MenuItem>
                        <MenuItem value={'false'}>Inactive</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>
                </CardContent>
                <FixedPositionButtons
                  actions={[
                    {
                      name: 'CANCEL',
                      onClick: redirectToSalesGroupList,
                      color: 'default' as 'default',
                      disabled: false,
                      icon: 'close',
                    },
                    {
                      name: !isUpdateMode ? `Create` : `Update`,
                      onClick: () => {
                        submitForm();
                      },
                      color: 'primary' as 'primary',
                      disabled: false,
                      icon: 'save',
                    },
                  ]}
                  open
                />
              </Form>
            );
          }}
        </Formik>
      </Loading>
    </PageContainer>
  );
};

export default SalesGroupForm;
